import type { TFunction } from 'i18next';
import api from '../../services/api';
import { PZDocumentDto, normalizePZDocument } from '../../dto/pz';
import { asOptionalString, asRecord, asString, isRecord } from '../../dto/common';
import { formatUnit } from '../../utils/units';

/**
 * Single line item on the storno preview: what will be zeroed and, when the
 * user has purchase-price visibility, what it is worth.
 */
export interface PZStornoPreviewItem {
  id: string;
  productName: string;
  productCode?: string;
  unit: string;
  quantityReceived: number;
  quantityRemaining: number;
  quantityRemainingPcs: number | null;
  quantityRemainingM2: number | null;
  quantityRemainingM3: number | null;
  /** Absent (not 0) when the caller cannot view purchase prices. */
  remainingValuePLN?: number;
}

export interface PZStornoBlockerIssue {
  wzDocumentId: string;
  wzNumber: string;
  productName: string;
  quantity: number;
  unit: string;
}

export interface PZStornoBlockerReservation {
  orderId: string;
  orderNumber: string;
  productName: string;
  quantity: number;
  unit: string;
}

export interface PZStornoBlockers {
  issues: PZStornoBlockerIssue[];
  reservations: PZStornoBlockerReservation[];
}

export interface PZStornoPreview {
  status: string;
  canStorno: boolean;
  blockedCode: string | null;
  blockers: PZStornoBlockers;
  items: PZStornoPreviewItem[];
  /** Absent (not 0) when the caller cannot view purchase prices. */
  totalRemainingValuePLN?: number;
  supplierOrder: { id: string; orderNumber: string } | null;
}

function optionalNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function nullableNumber(value: unknown): number | null {
  const n = optionalNumber(value);
  return n === undefined ? null : n;
}

function normalizeBlockerIssue(raw: unknown): PZStornoBlockerIssue {
  const record = asRecord(raw);
  return {
    wzDocumentId: asString(record?.wzDocumentId),
    wzNumber: asString(record?.wzNumber),
    productName: asString(record?.productName),
    quantity: optionalNumber(record?.quantity) ?? 0,
    unit: asString(record?.unit),
  };
}

function normalizeBlockerReservation(raw: unknown): PZStornoBlockerReservation {
  const record = asRecord(raw);
  return {
    orderId: asString(record?.orderId),
    orderNumber: asString(record?.orderNumber),
    productName: asString(record?.productName),
    quantity: optionalNumber(record?.quantity) ?? 0,
    unit: asString(record?.unit),
  };
}

function normalizePreviewItem(raw: unknown): PZStornoPreviewItem {
  const record = asRecord(raw);
  return {
    id: asString(record?.id),
    productName: asString(record?.productName),
    productCode: asOptionalString(record?.productCode),
    unit: asString(record?.unit),
    quantityReceived: optionalNumber(record?.quantityReceived) ?? 0,
    quantityRemaining: optionalNumber(record?.quantityRemaining) ?? 0,
    quantityRemainingPcs: nullableNumber(record?.quantityRemainingPcs),
    quantityRemainingM2: nullableNumber(record?.quantityRemainingM2),
    quantityRemainingM3: nullableNumber(record?.quantityRemainingM3),
    // Missing (undefined) must stay missing — a viewer without purchase-price
    // visibility gets no key at all from the backend, and collapsing that to 0
    // would silently print "0 PLN" instead of hiding the column.
    remainingValuePLN: optionalNumber(record?.remainingValuePLN),
  };
}

export function normalizePZStornoBlockers(raw: unknown): PZStornoBlockers {
  const record = asRecord(raw);
  const issues = Array.isArray(record?.issues) ? record!.issues.map(normalizeBlockerIssue) : [];
  const reservations = Array.isArray(record?.reservations)
    ? record!.reservations.map(normalizeBlockerReservation)
    : [];
  return { issues, reservations };
}

export function normalizePZStornoPreview(raw: unknown): PZStornoPreview {
  const record = asRecord(raw) || {};
  const items = Array.isArray(record.items) ? record.items.map(normalizePreviewItem) : [];
  const supplierOrderRecord = asRecord(record.supplierOrder);

  return {
    status: asString(record.status),
    canStorno: record.canStorno === true,
    blockedCode: asOptionalString(record.blockedCode) ?? null,
    blockers: normalizePZStornoBlockers(record.blockers),
    items,
    totalRemainingValuePLN: optionalNumber(record.totalRemainingValuePLN),
    supplierOrder: supplierOrderRecord
      ? { id: asString(supplierOrderRecord.id), orderNumber: asString(supplierOrderRecord.orderNumber) }
      : null,
  };
}

export async function loadPZStornoPreview(id: string): Promise<PZStornoPreview> {
  const { data } = await api.get(`/pz/${encodeURIComponent(id)}/storno-preview`);
  return normalizePZStornoPreview(data);
}

export interface PZStornoPayload {
  reason: string;
  expectedUpdatedAt: string;
  confirmOperationalException?: boolean;
  operationalExceptionFingerprint?: string;
  operationalExceptionReason?: string;
}

export async function stornoPZDocument(id: string, payload: PZStornoPayload): Promise<PZDocumentDto> {
  const { data } = await api.post(`/pz/${encodeURIComponent(id)}/storno`, payload);
  return normalizePZDocument(data);
}

/**
 * Turns the blocked-storno payload into one readable sentence per blocker
 * kind, naming the WZ numbers/quantities or order numbers/quantities. Reused
 * by both the dialog's inline alert and the disabled-button tooltip on
 * PZDetail so the wording never drifts between the two surfaces.
 */
export function describePZStornoBlockers(
  blockers: PZStornoBlockers | null | undefined,
  t: TFunction,
): string[] {
  if (!blockers) return [];
  const messages: string[] = [];

  if (blockers.issues.length > 0) {
    const list = blockers.issues
      .map((issue) => `${issue.wzNumber} (${issue.productName}: ${issue.quantity} ${formatUnit(issue.unit, 'pl')})`)
      .join(', ');
    messages.push(String(t('pz:storno.blockedIssued', { list })));
  }

  if (blockers.reservations.length > 0) {
    const list = blockers.reservations
      .map((reservation) => `${reservation.orderNumber} (${reservation.productName}: ${reservation.quantity} ${formatUnit(reservation.unit, 'pl')})`)
      .join(', ');
    messages.push(String(t('pz:storno.blockedReserved', { list })));
  }

  return messages;
}

// Re-exported so callers do not need to reach into '../../dto/common' just to
// narrow an unknown response shape before it reaches this module's normalizer.
export { isRecord };
